// File: Pages/Reports/utils/formConfigs.js

/**
 * Form Configuration Schema
 * 
 * This file defines the schema for form configurations and provides
 * default configurations for the different report types.
 */

import { REPORT_TYPES } from './reportTypes'

// Schema documentation for reference
export const formConfigSchema = {
  // Array of field definitions
  fields: [
    {
      // Required field properties
      name: 'String - unique field identifier',
      type: 'String - field type (text, textarea, select, checkbox, date, multi-checkbox)',
      
      // Optional field properties
      label: 'String - field label shown to user',
      subtitle: 'String - descriptive text under the label',
      placeholder: 'String - placeholder text for input fields',
      required: 'Boolean - whether field is required',
      defaultValue: 'Any - initial value for the field',
      
      // Validation
      validation: 'String or Function - validation type (url, youtube) or custom validation function',
      
      // Select and multi-checkbox options
      options: 'Array - static options for select or multi-checkbox fields',
      
      // Dynamic data sources
      sourceType: 'String - data source type (artists, albums)',
      sourceIdFrom: 'String - where to get the ID value (artist)',
      
      // Conditional visibility
      visibleWhen: {
        dependsOn: 'String - field name this visibility depends on',
        condition: 'String - condition type (equals, notEquals)',
        value: 'Any - value to compare against'
      },
      
      // Fields affected by this field's changes
      affectsFields: [
        'String - field name to reset when this field changes',
        // OR
        {
          fieldName: 'String - field to affect',
          condition: 'String - condition type (equals, notEquals)',
          resetWhen: 'Any - value that triggers reset'
        }
      ],
      
      // Additional options
      searchable: 'Boolean - whether to show search box for multi-checkbox fields'
    }
  ],
  
  // Form-level configuration
  usePreviousPage: 'Boolean - whether to use previous page preview',
  previousPageField: 'String - field to update with previous page URL',
  includeArtistName: 'Boolean - whether to include artist name in submission data',
  submitButtonLabel: 'String - text for submit button',
  
  // Custom form-level validation
  formValidation: 'Function - custom validation logic for the entire form'
}

// Default form configurations for each report type
const formConfigs = {
  // Incorrect Content Form Config
  [REPORT_TYPES.INCORRECT_CONTENT]: {
    fields: [
      {
        name: 'feelsItemLink',
        type: 'text',
        label: 'Link to Feels Item',
        subtitle: 'Provide a link to the Feels Item for your request',
        placeholder: 'Enter URL or item ID',
        required: true,
        validation: 'url'
      },
      {
        name: 'youtubeItemLink',
        type: 'text',
        label: 'YouTube Item Link',
        subtitle: 'Provide the YouTube music link to the item with the correct data',
        placeholder: 'Enter YouTube URL',
        required: true,
        validation: 'youtube'
      },
      {
        name: 'notes',
        type: 'textarea',
        label: 'Notes',
        subtitle: 'Please leave a detailed description of your request',
        placeholder: 'Describe the issue or requested changes in detail...',
        required: false
      }
    ],
    usePreviousPage: true,
    previousPageField: 'feelsItemLink',
    submitButtonLabel: 'Submit Report'
  },
  
  // Request Access Form Config
  [REPORT_TYPES.ADD_ARTIST_TO_ORG]: {
    fields: [
      {
        name: 'artistId',
        type: 'select',
        label: 'Artist',
        subtitle: 'Please choose the artist you are requesting access to',
        required: true,
        sourceType: 'artists',
        sourceIdFrom: 'artist',
        searchable: true,
        searchAll: true,
        useCustomSelect: true  
      },
      {
        name: 'organizationName',
        type: 'text',
        label: 'New Organization Name',
        subtitle: 'What is the name of your label/organization?',
        placeholder: 'Enter organization name',
        required: true
      },
      {
        name: 'labelStartDate',
        type: 'date',
        label: 'New Label Start Date',
        subtitle: 'When will/did this artist join your label?',
        required: true
      },
      {
        name: 'rightsCarryForward',
        type: 'select',
        label: 'Do all of the rights carry forward to the new label?',
        subtitle: 'Please select whether all rights transfer to your label',
        options: ['yes', 'no'],
        defaultValue: 'yes',
        required: true,
        affectsFields: ['priorReleases']
      },
      {
        name: 'priorReleases',
        type: 'multi-checkbox',
        label: 'What prior releases from this artist does your label have rights to?',
        subtitle: 'Select all releases that your label has rights to',
        sourceType: 'albums',
        required: true,
        searchable: true,
        useCustomSelect: true,  
        defaultValue: [],
        visibleWhen: {
          dependsOn: 'rightsCarryForward',
          condition: 'equals',
          value: 'no'
        }
      },
      {
        name: 'previousLabelName',
        type: 'text',
        label: 'Artists Previous Label Name',
        placeholder: 'Enter previous label name',
        required: true
      },
      {
        name: 'previousLabelStartDate',
        type: 'date',
        label: 'Start Date At Previous Label',
        required: false
      },
      {
        name: 'previousLabelEndDate',
        type: 'date',
        label: 'End Date At Previous Label',
        required: false
      },
      {
        name: 'notes',
        type: 'textarea',
        label: 'Notes',
        subtitle: 'Please leave a detailed description of your request',
        placeholder: 'Add any additional details about your access request...',
        required: false
      }
    ],
    usePreviousPage: false,
    includeArtistName: true,
    submitButtonLabel: 'Submit Request',
    formValidation: (formState, errors) => {
      const newErrors = { ...errors }
      
      // Validate date ranges if both are provided
      if (formState.previousLabelStartDate && formState.previousLabelEndDate) {
        const startDate = new Date(formState.previousLabelStartDate)
        const endDate = new Date(formState.previousLabelEndDate)
        
        if (endDate < startDate) {
          newErrors.previousLabelEndDate = 'End date must be after start date'
        }
      }
      
      // Validate prior releases are selected when rights do not carry forward
      if (formState.rightsCarryForward === 'no' && formState.priorReleases.length === 0) {
        newErrors.priorReleases = 'Please select at least one release'
      }
      
      return newErrors
    }
  },

  // Missing Content Form Configuration
  [REPORT_TYPES.MISSING_CONTENT]: {
    fields: [
      {
        name: 'youtubeItemLink',
        type: 'text',
        label: 'YouTube Item Link',
        subtitle: 'Provide the YouTube music link to the missing item',
        placeholder: 'Enter YouTube URL',
        required: true,
        validation: 'youtube'
      },
      {
        name: 'artistId',
        type: 'select',
        label: 'Artist',
        subtitle: 'Please choose which of your artists this content is missing from',
        required: true,
        sourceType: 'artists',
        sourceIdFrom: 'artist',
        searchable: true,
        useCustomSelect: true 
      },
      {
        name: 'notes',
        type: 'textarea',
        label: 'Notes',
        subtitle: 'Please leave a detailed description of your request',
        placeholder: 'Provide any additional details about the missing content...',
        required: false
      }
    ],
    usePreviousPage: false,
    includeArtistName: true,
    submitButtonLabel: 'Submit Report'
  },
  
  // Disassociate Content Form Configuration
  [REPORT_TYPES.DISASSOCIATE_CONTENT]: {
    fields: [
      {
        name: 'artistId',
        type: 'select',
        label: 'Artist',
        subtitle: 'Please choose the artist you want items removed from',
        required: true,
        sourceType: 'artists',
        sourceIdFrom: 'artist',
        searchable: true,
        useCustomSelect: true  
      },
      {
        name: 'feelsItemLink',
        type: 'text',
        label: 'Link to Feels Item',
        subtitle: 'Provide a link to the content you would like disassociated',
        placeholder: 'Enter URL or item ID',
        required: true,
        validation: 'url'
      },
      {
        name: 'notes',
        type: 'textarea',
        label: 'Notes',
        subtitle: 'Please leave a detailed description of your request',
        placeholder: 'Describe which content you\'d like to disassociate and why...',
        required: false
      }
    ],
    usePreviousPage: true,
    previousPageField: 'feelsItemLink',
    includeArtistName: true,
    submitButtonLabel: 'Submit Report'
  },
  
  // Other Form Configuration
  [REPORT_TYPES.OTHER]: {
    fields: [
      {
        name: 'feelsItemLink',
        type: 'text',
        label: 'Link to Feels Item',
        subtitle: 'Provide a link to the Feels Item for your request',
        placeholder: 'Enter URL or item ID (optional)',
        required: false,
        validation: 'url'
      },
      {
        name: 'youtubeItemLink',
        type: 'text',
        label: 'YouTube Item Link',
        subtitle: 'Provide the YouTube music link to the item with the correct data',
        placeholder: 'Enter YouTube URL (optional)',
        required: false,
        validation: 'youtube'
      },
      {
        name: 'artistId',
        type: 'select',
        label: 'Artist',
        subtitle: 'Please choose the artist you are reporting about',
        required: true,
        sourceType: 'artists',
        sourceIdFrom: 'artist',
        searchable: true,
        searchAll: true,
        useCustomSelect: true
      },
      {
        name: 'notes',
        type: 'textarea',
        label: 'Notes',
        subtitle: 'Please leave a detailed description of your request',
        placeholder: 'Describe your issue in detail...',
        required: false
      }
    ],
    usePreviousPage: true,
    previousPageField: 'feelsItemLink',
    includeArtistName: true,
    submitButtonLabel: 'Submit Report',
    formValidation: (formState, errors) => {
      const newErrors = { ...errors }
      
      // For the "Other" form, at least one field must be filled
      if (!formState.feelsItemLink && !formState.youtubeItemLink && 
          !formState.artistId && !formState.notes) {
        newErrors.notes = 'At least one field must be filled out'
      }
      
      return newErrors
    }
  }
}

export default formConfigs